import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import AuthHeader from '../../components/ui/AuthHeader';
import PaymentModal from '../../components/ui/PaymentModal';
import Button from '../../components/ui/Button';

import BusinessContextForm from './components/BusinessContextForm';
import RevenueDataForm from './components/RevenueDataForm';
import ServiceBusinessForm from './components/ServiceBusinessForm';
import ProductBusinessForm from './components/ProductBusinessForm';
import CostStructureForm from './components/CostStructureForm';
import GrowthRiskForm from './components/GrowthRiskForm';
import StrategicIntelligenceForm from './components/StrategicIntelligenceForm';
import AnalyticsDashboard from './components/AnalyticsDashboard';

const PredictiveAnalysis = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);

  const [formData, setFormData] = useState({
    // Step 1: Business Context
    businessType: '',
    yearsInOperation: '',
    industry: '',
    isSeasonal: '',
    peakMonths: [],
    primaryRevenueSource: '',
    businessModel: '',
    
    // Step 2: Revenue Data
    monthlyRevenue: Array(12)?.fill(''),
    averageGrowth: '',
    topProducts: [{ name: '', contribution: '' }, { name: '', contribution: '' }, { name: '', contribution: '' }],
    revenueSpike: { month: '', reason: '' },
    
    // Step 3A: Service Business
    totalServices: '',
    highestRevenueService: '',
    highestProfitService: '',
    avgDeliveryTime: '',
    customerRepeatRate: '',
    monthlyNewCustomers: '',
    avgTicketSize: '',
    teamSize: '',
    
    // Step 3B: Product Business
    totalSKUs: '',
    bestSellingProduct: '',
    lowestSellingProduct: '',
    inventoryTurnover: '',
    monthlyProductionCapacity: '',
    returnRate: '',
    grossMargin: '',
    stockOutFrequency: '',
    
    // Step 4: Cost Structure
    fixedExpenses: '',
    variableCosts: '',
    marketingSpend: '',
    operationalOverhead: '',
    debtObligations: '',
    cashReserve: '',
    
    // Step 5: Growth & Risk
    planningNewProduct: '',
    planningPriceIncrease: '',
    expansionToNewRegion: '',
    hiringPlans: '',
    regulatoryRisk: '',
    competitorPressure: '',
    
    // Step 6: Strategic Intelligence
    revenueBottleneck: '',
    doublingRevenue: '',
    trackCAC: '',
    trackLTV: '',
    majorClientDependency: ''
  });

  const updateFormData = (data) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const handleNext = () => {
    if (currentStep === 2 && (formData?.businessType === 'service' || formData?.businessType === 'hybrid')) {
      setCurrentStep(3);
    } else if (currentStep === 2 && formData?.businessType === 'product') {
      setCurrentStep(4);
    } else if (currentStep === 3 && formData?.businessType === 'hybrid') {
      setCurrentStep(4);
    } else if (currentStep === 3 && formData?.businessType === 'service') {
      setCurrentStep(5);
    } else if (currentStep === 4 && formData?.businessType === 'hybrid') {
      setCurrentStep(5);
    } else if (currentStep === 4 && formData?.businessType === 'product') {
      setCurrentStep(5);
    } else if (currentStep < 6) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowDashboard(true);
    }
  };

  const handleBack = () => {
    if (currentStep === 3 && formData?.businessType === 'product') {
      setCurrentStep(2);
    } else if (currentStep === 4 && formData?.businessType === 'service') {
      setCurrentStep(3);
    } else if (currentStep === 4 && formData?.businessType === 'hybrid') {
      setCurrentStep(3);
    } else if (currentStep === 5 && formData?.businessType === 'product') {
      setCurrentStep(4);
    } else if (currentStep === 5 && formData?.businessType === 'service') {
      setCurrentStep(3);
    } else if (currentStep === 5 && formData?.businessType === 'hybrid') {
      setCurrentStep(4);
    } else if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleExport = (type) => {
    setIsPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    console.log('Payment successful, exporting...');
  };

  const getStepTitle = () => {
    switch(currentStep) {
      case 1: return 'Business Context – Foundation Data';
      case 2: return 'Revenue & Performance Data (Last 12 Months)';
      case 3: return formData?.businessType === 'product' ? 'Product Business Operational Data' : 'Service Business Operational Data';
      case 4: return formData?.businessType === 'hybrid' ? 'Product Business Operational Data' : 'Cost & Financial Stability Data';
      case 5: return 'Cost & Financial Stability Data';
      case 6: return 'Growth & Risk Indicators';
      case 7: return 'Strategic Signals';
      default: return '';
    }
  };

  const renderStep = () => {
    if (currentStep === 1) {
      return <BusinessContextForm formData={formData} onUpdate={updateFormData} />;
    } else if (currentStep === 2) {
      return <RevenueDataForm formData={formData} onUpdate={updateFormData} />;
    } else if (currentStep === 3 && (formData?.businessType === 'service' || formData?.businessType === 'hybrid')) {
      return <ServiceBusinessForm formData={formData} onUpdate={updateFormData} />;
    } else if ((currentStep === 3 && formData?.businessType === 'product') || (currentStep === 4 && formData?.businessType === 'hybrid')) {
      return <ProductBusinessForm formData={formData} onUpdate={updateFormData} />;
    } else if ((currentStep === 4 && formData?.businessType === 'service') || (currentStep === 4 && formData?.businessType === 'product') || (currentStep === 5 && formData?.businessType === 'hybrid')) {
      return <CostStructureForm formData={formData} onUpdate={updateFormData} />;
    } else if ((currentStep === 5 && (formData?.businessType === 'service' || formData?.businessType === 'product')) || (currentStep === 6 && formData?.businessType === 'hybrid')) {
      return <GrowthRiskForm formData={formData} onUpdate={updateFormData} />;
    } else if ((currentStep === 6 && (formData?.businessType === 'service' || formData?.businessType === 'product')) || (currentStep === 7 && formData?.businessType === 'hybrid')) {
      return <StrategicIntelligenceForm formData={formData} onUpdate={updateFormData} />;
    }
  };

  if (showDashboard) {
    return (
      <div className="min-h-screen bg-background">
        <AuthHeader isAuthenticated userName="John Smith" userSubscription="free" />
        <Sidebar userSubscription="free" />
        <main className="lg:ml-60 pt-16">
          <AnalyticsDashboard formData={formData} onExport={handleExport} onBack={() => setShowDashboard(false)} />
        </main>
        <PaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          featureName="Export Reports"
          featureDescription="Download detailed PDF and Excel reports with full analytics"
          onSuccess={handlePaymentSuccess}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AuthHeader isAuthenticated userName="John Smith" userSubscription="free" />
      <Sidebar userSubscription="free" />

      <main className="lg:ml-60 pt-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
              Predictive Analysis
            </h1>
            <p className="text-sm md:text-base text-muted-foreground font-body">
              Collect business data in structured layers and generate intelligent financial forecasts
            </p>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Step {currentStep} of 6</span>
              <span className="text-sm text-muted-foreground">{Math.round((currentStep / 6) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / 6) * 100}%` }}
              />
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg elevation-sm p-6 md:p-8">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-6">
              {getStepTitle()}
            </h2>

            {renderStep()}

            <div className="flex gap-4 mt-8 pt-6 border-t border-border">
              {currentStep > 1 && (
                <Button
                  variant="outline"
                  onClick={handleBack}
                  iconName="ChevronLeft"
                  iconPosition="left"
                >
                  Back
                </Button>
              )}
              <Button
                variant="default"
                onClick={handleNext}
                iconName={currentStep === 6 ? "Check" : "ChevronRight"}
                iconPosition="right"
                className="ml-auto"
              >
                {currentStep === 6 ? 'Generate Analysis' : 'Next Step'}
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PredictiveAnalysis;